// src/pages/clubs/ClubMembers.jsx
// View, invite, and manage club members + roles.
// Route: /clubs/:clubId/members

import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  doc, setDoc, updateDoc, deleteDoc, collection,
  query, where, getDocs, serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { useAuth } from '@/context/AuthContext';
import useClub from '@/hooks/useClub';
import PageSpinner from '@/components/PageSpinner';
import ClubRoleBadge from '@/components/ClubRoleBadge';
import { normalizeClubRole, CLUB_ROLES, CLUB_ROLE_LABELS } from '@/constants/roles';
import toast from 'react-hot-toast';

import {
  ChevronLeftIcon,
  UserGroupIcon,
  PlusIcon,
  TrashIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

/* ------------------------------------------------------------------ */
/* Role select                                                          */
/*                                                                      */
/* Writes canonical `club_admin` / `member`. If a row still carries a   */
/* legacy `admin` string, we normalize it for the controlled-select     */
/* value so the UI doesn't render empty — picking "Admin" will then     */
/* persist the canonical `club_admin` on next save.                     */
/* ------------------------------------------------------------------ */
function RoleSelect({ currentRole, onChange, disabled }) {
  const canonical = normalizeClubRole(currentRole);
  const value = canonical === CLUB_ROLES.CLUB_ADMIN ? CLUB_ROLES.CLUB_ADMIN : 'member';
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      disabled={disabled}
      className="rounded-lg border border-zinc-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-[#0d1f14] focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30 disabled:opacity-50"
    >
      <option value={CLUB_ROLES.CLUB_ADMIN}>Admin</option>
      <option value="member">Member</option>
    </select>
  );
}

/* ------------------------------------------------------------------ */
/* Member row                                                           */
/* ------------------------------------------------------------------ */
function MemberRow({ member, canManage, isMe, isOwner: memberIsOwner, onRoleChange, onRemove }) {
  const [changingRole, setChangingRole] = useState(false);
  const [removing, setRemoving]         = useState(false);

  const handleRoleChange = async (newRole) => {
    setChangingRole(true);
    try {
      await onRoleChange(member.uid, newRole);
      toast.success('Role updated.');
    } catch {
      toast.error('Failed to update role.');
    } finally {
      setChangingRole(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm(`Remove ${member.displayName || member.email} from the club?`)) return;
    setRemoving(true);
    try {
      await onRemove(member.uid);
      toast.success('Member removed.');
    } catch {
      toast.error('Failed to remove member.');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="flex items-center gap-3 px-4 py-3.5 hover:bg-[#b5f03d]/5 transition-colors">
      {/* Avatar */}
      <div className="h-9 w-9 rounded-full bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 flex items-center justify-center flex-shrink-0">
        <span className="text-sm font-bold text-[#5a8a1f] uppercase">
          {(member.displayName || member.email || '?')[0]}
        </span>
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold text-[#0d1f14] truncate">
            {member.displayName || member.email}
            {isMe && <span className="ml-1 text-xs text-zinc-400">(you)</span>}
          </p>
          <ClubRoleBadge role={member.role} />
        </div>
        {member.displayName && member.email && (
          <p className="text-xs text-zinc-400 truncate">{member.email}</p>
        )}
      </div>

      {/* Actions */}
      {canManage && !memberIsOwner && !isMe && (
        <div className="flex items-center gap-2 flex-shrink-0">
          {changingRole ? (
            <ArrowPathIcon className="h-4 w-4 animate-spin text-zinc-400" />
          ) : (
            <RoleSelect
              currentRole={member.role}
              onChange={handleRoleChange}
              disabled={changingRole}
            />
          )}
          <button
            type="button"
            onClick={handleRemove}
            disabled={removing}
            className="p-1.5 rounded-lg text-zinc-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
            title="Remove member"
          >
            {removing ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <TrashIcon className="h-4 w-4" />}
          </button>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Invite form                                                          */
/* ------------------------------------------------------------------ */
function InviteForm({ clubId, clubName }) {
  const [email, setEmail]   = useState('');
  const [role,  setRole]    = useState('member');
  const [sending, setSending] = useState(false);
  const [sent, setSent]       = useState(false);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      // Check if a user with this email already exists
      const usersSnap = await getDocs(
        query(collection(db, 'users'), where('email', '==', email.trim().toLowerCase()))
      );

      if (!usersSnap.empty) {
        // User exists — add directly to members
        const userDoc = usersSnap.docs[0];
        const uid = userDoc.id;
        const data = userDoc.data();
        await setDoc(doc(db, 'clubs', clubId, 'members', uid), {
          uid,
          email:       data.email || email.trim().toLowerCase(),
          displayName: data.displayName || data.name || '',
          role,
          addedAt: serverTimestamp(),
        }, { merge: true });

        // ✅ Also update the user's profile so the Club section appears in their sidebar
        await setDoc(doc(db, 'users', uid), { clubId }, { merge: true });

        // Canonical roles (e.g. `club_admin`) aren't pretty in UI — use the
        // human label when one exists, otherwise fall through to the raw
        // value (e.g. `member`).
        const roleLabel = CLUB_ROLE_LABELS[role] || role;
        toast.success(`${data.displayName || email} added as ${roleLabel}.`);
      } else {
        // User doesn't exist yet — save a top-level pending invite (processed on signup)
        const inviteRef = doc(collection(db, 'clubPendingInvites'));
        await setDoc(inviteRef, {
          email:     email.trim().toLowerCase(),
          role,
          clubId,
          clubName,
          invitedAt: serverTimestamp(),
          status:    'pending',
        });
        toast.success(`Invite saved for ${email}. They'll be added when they sign up.`);
      }

      setSent(true);
      setEmail('');
      setRole('member');
      setTimeout(() => setSent(false), 3000);
    } catch (err) {
      console.error(err);
      toast.error('Failed to send invite. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={handleInvite} className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-5 space-y-4">
      <h3 className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]" style={{ fontFamily: HEAD_FONT }}>Invite a Member</h3>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="coach@email.com"
            required
            className="w-full pl-9 pr-3.5 py-2.5 rounded-xl border border-zinc-300 bg-white text-sm text-[#0d1f14] placeholder-zinc-400 focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30"
          />
        </div>
        <select
          value={role}
          onChange={e => setRole(e.target.value)}
          className="rounded-xl border border-zinc-300 bg-white px-3 py-2.5 text-sm font-medium text-[#0d1f14] focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30"
        >
          <option value="member">Member</option>
          <option value={CLUB_ROLES.CLUB_ADMIN}>Admin</option>
        </select>
        <button
          type="submit"
          disabled={sending || !email.trim()}
          className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-50 whitespace-nowrap"
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
        >
          {sending ? (
            <ArrowPathIcon className="h-4 w-4 animate-spin" />
          ) : sent ? (
            <CheckCircleIcon className="h-4 w-4" />
          ) : (
            <PlusIcon className="h-4 w-4" />
          )}
          {sending ? 'Adding…' : sent ? 'Added!' : 'Add Member'}
        </button>
      </div>

      <p className="text-xs text-zinc-400">
        If this person hasn't signed up yet, they'll automatically be added to the club when they create their account.
      </p>
    </form>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */
export default function ClubMembers() {
  const { clubId } = useParams();
  const { firebaseUser } = useAuth();
  const { club, members, isAdmin, isOwner, loading } = useClub(clubId);

  if (loading) return <PageSpinner />;

  if (!club) {
    return (
      <div className="min-h-screen bg-[#f7faf5] flex flex-col items-center justify-center text-center px-4">
        <ExclamationTriangleIcon className="h-12 w-12 text-zinc-300 mb-4" />
        <h2 className="text-lg font-semibold text-[#0d1f14]" style={{ fontFamily: HEAD_FONT }}>Club not found</h2>
        <Link to="/dashboard" className="text-sm text-[#5a8a1f] hover:underline">Back to Dashboard</Link>
      </div>
    );
  }

  // Look up teams in THIS club where the given member is the owner.
  // Used as a guard before removing or demoting a director — if they
  // own teams, those teams would be orphaned without club-level admin
  // taking over, so we warn the caller first.
  //
  // Member queries are server-staff already (ClubMembers is admin-gated)
  // so they can read club + teams safely. Phase 2 rules require a
  // teamId-or-equivalent gate — clubId is the gate here, since the
  // teams rule lets club-staff read team docs via `isClubStaff(team.clubId)`.
  const findOwnedTeamsInClub = async (uid) => {
    if (!uid || !clubId) return [];
    try {
      const snap = await getDocs(query(
        collection(db, 'teams'),
        where('clubId',  '==', clubId),
        where('ownerId', '==', uid),
      ));
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (err) {
      console.warn('[ClubMembers] owned-teams lookup failed', err?.message);
      return [];
    }
  };

  const handleRoleChange = async (uid, newRole) => {
    // If demoting a director (owner / club_admin / admin) to a non-staff
    // role, warn first if they own teams under this club. Promoting up
    // doesn't need this guard.
    const member = members.find((m) => m.uid === uid);
    const wasStaff = ['owner', 'director', 'club_admin', 'admin']
      .includes(String(member?.role || '').toLowerCase());
    const willBeStaff = ['owner', 'director', 'club_admin', 'admin']
      .includes(String(newRole || '').toLowerCase());
    if (wasStaff && !willBeStaff) {
      const owned = await findOwnedTeamsInClub(uid);
      if (owned.length > 0) {
        const teamList = owned.map((t) => `  • ${t.name || t.id}`).join('\n');
        const ok = window.confirm(
          `${member?.displayName || member?.email || 'This member'} owns ${owned.length} team${owned.length === 1 ? '' : 's'} in this club:\n\n${teamList}\n\n` +
          `Demoting them to ${newRole} won't transfer those teams — they stay attached to the club but this person remains the team owner. ` +
          `For a clean handoff, transfer team ownership FIRST in Team Settings, then change their club role.\n\nProceed anyway?`
        );
        if (!ok) return;
      }
    }
    try {
      await updateDoc(doc(db, 'clubs', clubId, 'members', uid), { role: newRole });
    } catch (err) {
      console.error('[ClubMembers] role change failed', err);
      toast.error('Could not change role.');
    }
  };

  const handleRemove = async (uid) => {
    // Same guard for outright removal — but the consequences are worse
    // than a demotion, so we ALWAYS show the team list (not just a
    // generic "remove?" confirm).
    const member = members.find((m) => m.uid === uid);
    const owned = await findOwnedTeamsInClub(uid);
    if (owned.length > 0) {
      const teamList = owned.map((t) => `  • ${t.name || t.id}`).join('\n');
      const ok = window.confirm(
        `${member?.displayName || member?.email || 'This member'} owns ${owned.length} team${owned.length === 1 ? '' : 's'} in this club:\n\n${teamList}\n\n` +
        `Removing them from the club WON'T remove the teams — but those teams will lose their owner's club-level access. ` +
        `For a clean exit, transfer team ownership to another coach FIRST, then remove this member.\n\nRemove anyway?`
      );
      if (!ok) return;
    }
    try {
      await deleteDoc(doc(db, 'clubs', clubId, 'members', uid));
    } catch (err) {
      console.error('[ClubMembers] remove failed', err);
      toast.error('Could not remove member.');
    }
  };

  // Sort: director first, then club admins, then everyone else. We
  // normalize the stored role so legacy docs (`owner` / `admin`) rank
  // correctly alongside canonical ones (`director` / `club_admin`).
  const rank = (role) => {
    const canonical = normalizeClubRole(role);
    if (canonical === CLUB_ROLES.DIRECTOR)   return 0;
    if (canonical === CLUB_ROLES.CLUB_ADMIN) return 1;
    return 2;
  };
  const sorted = [...members].sort((a, b) => rank(a.role) - rank(b.role));

  return (
    <div className="min-h-screen bg-[#f7faf5]">
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">

        {/* ── Header ─────────────────────────────────── */}
        <div className="flex items-center gap-3">
          <Link
            to={`/clubs/${clubId}`}
            className="p-2 rounded-xl text-zinc-400 hover:text-[#5a8a1f] hover:bg-[#b5f03d]/10 transition-colors"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </Link>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 flex items-center justify-center">
              <UserGroupIcon className="h-6 w-6 text-[#5a8a1f]" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5a8a1f]" style={{ fontFamily: HEAD_FONT }}>Club</p>
              <h1 className="text-2xl font-bold uppercase tracking-tight text-[#0d1f14]" style={{ fontFamily: HEAD_FONT }}>Members</h1>
              <p className="text-xs text-zinc-500">{club.name} · {members.length} member{members.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
        </div>

        {/* ── Invite form (admins only) ───────────────── */}
        {isAdmin && <InviteForm clubId={clubId} clubName={club.name} />}

        {/* ── Member list ────────────────────────────── */}
        <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-100">
            <p className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]" style={{ fontFamily: HEAD_FONT }}>
              {members.length} Member{members.length !== 1 ? 's' : ''}
            </p>
          </div>
          {sorted.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-zinc-400">
              No members yet. Invite someone above.
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {sorted.map(m => (
                <MemberRow
                  key={m.uid}
                  member={m}
                  canManage={isAdmin}
                  isMe={m.uid === firebaseUser?.uid}
                  isOwner={normalizeClubRole(m.role) === CLUB_ROLES.DIRECTOR}
                  onRoleChange={handleRoleChange}
                  onRemove={handleRemove}
                />
              ))}
            </div>
          )}
        </div>

        {/* ── Role legend ────────────────────────────── */}
        <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-4 space-y-2">
          <p className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-2" style={{ fontFamily: HEAD_FONT }}>Role Permissions</p>
          {[
            { role: CLUB_ROLES.DIRECTOR,   desc: 'Full access. Can edit settings, manage members, delete club.' },
            { role: CLUB_ROLES.CLUB_ADMIN, desc: 'Can invite members, change roles (except director), view all teams.' },
            { role: 'member',              desc: 'View-only access to club dashboard and team list.' },
          ].map(({ role, desc }) => (
            <div key={role} className="flex items-start gap-2">
              <ClubRoleBadge role={role} />
              <p className="text-xs text-zinc-500 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
